<template>
    <div :class="['tribute-view', { confetti: confetti }]" v-if="tributeVideo">
        <template v-if="!pinError">
            <TributeStepper
                :key="`${stepHashes.join('-')}-${stepperRefreshKey}`"
                :disabled="!tributeVideo.id || conditionallyDisableForContributors"
                :currentStep="currentStep"
                :stepData="steps"
                @change-step="val => handleChangeStep(val)"
                @init-rerender="initRerender"
                @profile-error="profileError = true"
            >
                <template
                    v-slot:extras
                    v-if="!isFamilyPage && !isContributorPage && !isMobile && currentStep !== 'setup'"
                >
                    <div class="d-flex align-center submissions-control">
                        <small class="label">Submissions:</small>
                        <v-switch
                            v-model="tributeVideo.submissionsOpen"
                            readonly
                            color="green"
                            @click="submissionsOpenChangeModal = true"
                        >
                            <template v-slot:label>
                                <span class="m-0 p-0">{{ tributeVideo.submissionsOpen ? 'Open' : 'Closed' }}</span>
                            </template>
                        </v-switch>
                        <v-dialog v-model="submissionsOpenChangeModal" max-width="420">
                            <v-card>
                                <v-card-title class="text-h5">
                                    {{ tributeVideo.submissionsOpen ? 'Close' : 'Open' }} submissions?
                                </v-card-title>
                                <v-card-text>
                                    Please confirm that you would like to
                                    <strong>{{ tributeVideo.submissionsOpen ? 'close' : 'open' }}</strong>
                                    submissions.
                                    <template v-if="standardRender?.status === 2 || publicRender?.status === 2">
                                        <br />
                                        <p>
                                            The tribute will need to be re-rendered in order to apply any new
                                            contributions.
                                        </p>
                                    </template>
                                </v-card-text>
                                <v-card-actions>
                                    <v-spacer></v-spacer>
                                    <v-btn text @click="submissionsOpenChangeModal = false"> Cancel </v-btn>
                                    <v-btn text @click="setSubmissionsOpen(!tributeVideo.submissionsOpen)">
                                        {{ tributeVideo.submissionsOpen ? 'Close' : 'Open' }} Submissions
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>
                    </div>
                </template>
            </TributeStepper>

            <div class="main-content" :class="[`section-${currentStep}`, { expanded: tributeEditorExpanded }]">
                <!-- STEP CONTENT: START -->
                <div class="step-content">
                    <ChangelogPanel
                        v-if="token && tributeVideo.id && !isFamilyPage && !isContributorPage && !isMobile"
                        v-show="currentStep !== 'setup'"
                        class="my-3"
                        :tributeVideoId="tributeVideo.id"
                        @init-rerender="initRerender"
                    >
                        <template #activator="{ onClick }">
                            <v-fade-transition>
                                <div v-show="showOutOfSyncWarning" class="changelog-control">
                                    <div
                                        @click="onClick"
                                        class="rounded-pill d-flex align-center out-of-sync-warning-outer"
                                    >
                                        <div class="rounded-pill changes-detected-pill">
                                            <span>Changes Detected</span>
                                        </div>
                                        <span>{{ outOfSyncChanges.length }} changes since last render </span>
                                        <font-awesome-icon
                                            class="mr-2"
                                            style="font-size: 1rem; color: #f97316"
                                            icon="fa-regular fa-chevron-right"
                                        ></font-awesome-icon>
                                    </div>
                                </div>
                            </v-fade-transition>
                        </template>
                    </ChangelogPanel>

                    <template v-if="showDeadline && forceExpandedWorkspace">
                        <TributeDeadlineChip :bg-color="'white'" />
                    </template>

                    <template v-if="$route.params.slug">
                        <TributeSetupForContributors
                            v-if="isFamilyPage || isContributorPage"
                            v-show="currentStep === 'setup'"
                            :serviceSlug="$route.params.slug"
                            :tributeVideo="tributeVideo"
                            :sessionUser="sessionUser"
                            @next-step="handleChangeStep(nextStep.hash)"
                            @setAnonUserDetails="handleSetAnonUserDetails"
                            @update-session-user="data => updateSessionUser(data)"
                        />
                        <TributeSetup
                            v-else
                            v-show="currentStep === 'setup'"
                            :isActiveTab="currentStep === 'setup'"
                            :serviceSlug="$route.params.slug"
                            :tributeVideo="tributeVideo"
                            @tribute-video-created="data => handleTributeCreated(data)"
                            @next-step="handleChangeStep(nextStep.hash)"
                            @setAnonUserDetails="handleSetAnonUserDetails"
                        />
                    </template>

                    <ManageSummary
                        ref="manageSummary"
                        v-show="currentStep === 'create'"
                        v-if="token && tributeVideo.id"
                        :tributeVideo="tributeVideo"
                        :slideChanges="slideChanges"
                        :mainPhotoChanges="mainPhotoChanges"
                        :themeChanges="themeChanges"
                        :musicChanges="musicChanges"
                        @render-started="onRenderStarted"
                    >
                        <template v-slot:previewPlayer>
                            <div class="summary-preview">
                                <div class="preview-player">
                                    <CreatomatePreview
                                        @reload="debouncedGeneratePreviewJson(tributeVideo.id)"
                                        ref="summaryCreatomatePreview"
                                        :source="previewSource"
                                        :modifications="previewMods"
                                    />
                                </div>
                            </div>
                        </template>
                    </ManageSummary>

                    <div v-show="currentStep === 'final-video'" class="w-100 final-video-container">
                        <!-- <p v-if="tributeVideo.id && !loading && !tributeVideo.submissionsOpen" class="text-center">
                            Submissions are
                            <template v-if="!isFamilyPage && !isContributorPage">
                                <a @click.prevent="openSettingsModal" class="status-tag"
                                    >closed <font-awesome-icon icon="fa-regular fa-cog" /> </a
                                >,
                            </template>
                            <template v-else>closed,</template> please watch the final video below
                        </p> -->
                        <div class="d-flex flex-column align-center">
                            <ManageFinalVideo
                                v-if="tributeVideo.id && token"
                                :tributeVideo="tributeVideo"
                                :isActiveTab="currentStep === 'final-video'"
                                @refresh-tribute="tryFetchTributeVideo($route.params.slug)"
                            />
                        </div>
                    </div>

                    <div
                        v-show="!['setup', 'final-video', 'create'].includes(currentStep)"
                        :class="['middle-content', { expanded: tributeEditorExpanded }]"
                    >
                        <div class="action-column">
                            <v-btn
                                @click="handleChangeStep(prevStep.hash)"
                                v-show="!isContributorPage"
                                :disabled="!prevStep"
                            >
                                <v-icon left> mdi-chevron-left </v-icon>
                                Prev
                            </v-btn>
                        </div>

                        <div
                            v-if="performanceMode !== tributePerformanceModeEnum.PERFORMANCE"
                            v-show="!tributeEditorExpanded && mounted"
                            class="center-column"
                        >
                            <div v-if="performanceMode === tributePerformanceModeEnum.BALANCED">
                                You'll need to manually refresh to preview changes.
                                <font-awesome-icon icon="far fa-share" class="fa-rotate-90"></font-awesome-icon>
                            </div>
                            <div class="preview-player-container">
                                <div class="preview-player" v-if="!isMobile && !isContributorPage">
                                    <TributeRenderOverlay
                                        v-if="showRenderOverlay"
                                        @playPreview="playPreview"
                                        @pausePreview="pausePreview"
                                        @change-step="val => handleChangeStep(val)"
                                    ></TributeRenderOverlay>

                                    <CreatomatePreview
                                        v-if="token && tributeVideo?.id"
                                        @reload="debouncedGeneratePreviewJson(tributeVideo.id)"
                                        @time-update="val => handleCreatomatePreviewTimeUpdate(val)"
                                        ref="mainCreatomatePreview"
                                        v-show="currentStep !== 'final-video'"
                                        :source="previewSource"
                                        :modifications="previewMods"
                                        bordered
                                        :showManualRefresh="performanceMode === tributePerformanceModeEnum.BALANCED"
                                    />
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <MobilePreview v-if="!isMobile">
                                <template #activator style="max-width: 500px; width: 30%">
                                    <font-awesome-icon icon="fa-regular fa-play-circle" class="fa-lg mr-2 play-icon" />
                                    Preview Video
                                </template>
                            </MobilePreview>
                        </div>
                        <v-btn
                            v-if="miniPreviewVisible"
                            @click="togglePreview"
                            color="white"
                            class="text-normal"
                            style="max-width: 500px; width: 30%"
                        >
                            <font-awesome-icon icon="fa-regular fa-play-circle" class="fa-lg mr-2 play-icon" />
                            Preview Video
                        </v-btn>
                        <div class="action-column">
                            <v-btn
                                class="next-btn"
                                @click="handleChangeStep(nextStep.hash)"
                                v-show="nextStep && !isContributorPage"
                                :disabled="currentStep === 'setup'"
                            >
                                Next
                                <v-icon right> mdi-chevron-right </v-icon>
                            </v-btn>
                        </div>
                    </div>
                </div>

                <!-- STEP CONTENT: START -->

                <!-- LOWER WORKSPACE: START -->
                <div class="workspace">
                    <v-tabs-items
                        v-show="currentStep !== 'setup'"
                        v-model="currentStep"
                        touchless
                        :touch="tabTouchEvents"
                    >
                        <v-tab-item transition="none" value="slides">
                            <ResizableWorkspace v-if="token && tributeVideo.eventId && stepHashes.includes('slides')">
                                <ManagePhotos
                                    ref="managePhotos"
                                    :eventId="tributeVideo.eventId"
                                    :tributeVideo="tributeVideo"
                                    @pause-preview="pausePreview"
                                    @refresh-preview="previewRefreshEmitHandler"
                                    @set-preview-timestamp="val => updatePreviewTimestamp(val)"
                                    :read-only="lockdownSlidesPage"
                                    :windowResizeCounter="windowResizeCounter"
                                    :preview-current-time="previewCurrentTime"
                                />
                            </ResizableWorkspace>
                        </v-tab-item>
                        <v-tab-item transition="none" value="explore-media">
                            <ResizableWorkspace
                                v-if="token && tributeVideo.eventId && stepHashes.includes('explore-media')"
                            >
                                <ManagePhotos
                                    ref="exploreMedia"
                                    :eventId="tributeVideo.eventId"
                                    :tributeVideo="tributeVideo"
                                    @pause-preview="pausePreview"
                                    :read-only="true"
                                />
                            </ResizableWorkspace>
                        </v-tab-item>
                        <v-tab-item transition="none" :value="'theme'">
                            <ResizableWorkspace v-if="token && tributeVideo.eventId && tributeVideo.id">
                                <ManageThemes
                                    :eventId="tributeVideo.eventId"
                                    :tributeVideo="tributeVideo"
                                    @pause-preview="pausePreview"
                                    @refresh-preview="previewRefreshEmitHandler"
                                    :read-only="lockdownSlidesPage"
                                    @change-step="val => handleChangeStep(val)"
                                />
                            </ResizableWorkspace>
                        </v-tab-item>
                        <v-tab-item transition="none" :value="'music'">
                            <ResizableWorkspace v-if="token && tributeVideo.id">
                                <ManageMusic
                                    :key="manageMusicRefreshKey"
                                    :eventId="tributeVideo.eventId"
                                    @refresh-preview="previewRefreshEmitHandler"
                                    :tributeVideo="tributeVideo"
                                    :read-only="lockdownSlidesPage"
                                />
                            </ResizableWorkspace>
                        </v-tab-item>
                    </v-tabs-items>
                </div>
                <!-- LOWER WORKSPACE: END -->

                <v-overlay opacity="0.7" :dark="false" v-model="showEditModeOverlay" absolute style="z-index: 11">
                    <div class="d-flex flex-column align-items-center">
                        <v-card color="white" class="p-2 m-3" max-width="500">
                            <v-card-title style="color: rgba(0, 0, 0, 0.87)"> Enable Edit Mode? </v-card-title>
                            <v-card-text style="color: rgba(0, 0, 0, 0.6)"
                                >This tribute video has been finalized. Do you want to make changes?
                            </v-card-text>

                            <v-card-actions class="d-flex justify-center">
                                <v-btn class="secondary-btn" @click="cancelMakeChanges" depressed>Cancel</v-btn>

                                <v-btn
                                    class="primary-btn"
                                    @click="dismissEditModeWarning"
                                    depressed
                                    color="#ff530d"
                                    dark
                                    >Make Changes</v-btn
                                >
                            </v-card-actions>
                        </v-card>
                    </div>
                </v-overlay>
            </div>

            <MiniMusicPlayer ref="miniMusicPlayer" :token="token" v-if="token" :disabled="lockdownSlidesPage" />
        </template>
        <template v-else>
            <div class="tribute-error-display">
                <img src="@/assets/tribute-logo.png" alt="Logo" />
                <div>{{ pinError ? 'Incorrect pin' : '????' }}</div>
            </div>
        </template>

        <v-dialog v-model="showSubmissionsClosedOverlay" persistent max-width="500">
            <v-card>
                <v-card-title class="d-flex justify-center">
                    <span>Submissions are Closed </span>
                </v-card-title>
                <v-card-text>
                    <div v-if="tributeVideo.funeralHomeLogo" class="fh-branding mb-5 d-flex">
                        <img :src="tributeVideo.funeralHomeLogo" class="fh-logo" />
                    </div>

                    <p>
                        {{ tributeVideo.funeralHomeName ?? 'The Funeral Home' }} is currently working on building this
                        video, and is not accepting any new photos.
                    </p>
                </v-card-text>
                <v-card-actions class="d-flex justify-center">
                    <v-btn class="secondary-btn" @click="dismissEditModeWarning" depressed>Dismiss</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <TributeDebugInfo v-if="debugInfoEnabled" :slides="$refs.managePhotos?.reversedSlides || []" />
    </div>
</template>

<script>
import initApiServices from '@/services/ApiInitializer';
import TributeVideoService from '@/services/tributeVideo.service';
import { mapActions } from 'pinia';

import TributeStepper from '@/components/Tribute/TributeStepper.vue';
import TributeSetup from '@/components/Tribute/TributeSetup.vue';
import TributeSetupForContributors from '@/components/Tribute/TributeSetupForContributors.vue';
import ManagePhotos from '@/components/Tribute/ManagePhotos.vue';
import ManageThemes from '@/components/Tribute/ManageThemes.vue';
import MiniMusicPlayer from '@/components/Tribute/Music/MiniMusicPlayer.vue';
import CreatomatePreview from '@/components/Tribute/Themes/CreatomatePreview.vue';
import ManageMusic from '@/components/Tribute/ManageMusic.vue';
import StepCreate from '@/components/Tribute/StepCreate.vue';
import ManageFinalVideo from '@/components/Tribute/ManageFinalVideo.vue';
import ResizableWorkspace from '@/components/Tribute/Layout/ResizableWorkspace.vue';
import TributeRenderOverlay from '@/components/Tribute/RenderOverlay/RenderOverlay.vue';
import ChangelogPanel from '@/components/Tribute/Layout/ChangelogPanel.vue';
import ManageSummary from '@/components/Tribute/ManageSummary.vue';
import {
    tributeChangelogActionEnums,
    creatomateRenderStatusEnum,
    tributeWorkflowEnum,
    tributePerformanceModeEnum,
} from '../../constants';
import { debounce, find, findIndex, isEmpty, deburr } from 'lodash';
import TributeDeadlineChip from '@/components/Tribute/TributeDeadlineChip.vue';
import TributeDebugInfo from '@/components/Tribute/AdminPanels/TributeDebugInfo.vue';
import { getTributeUploadSessionId, setTributeUploadSessionId } from '../../utilities/session';
const renderStatusDict = ['Planned', 'Rendering', 'Succeeded', 'Failed', 'Deleted'];
import { useTributeNotifications } from '@/stores/tributeNotifications';
import { useMiniPlayer } from '@/stores/miniPlayer';
import { getTributePerformanceMode } from '../../utilities/session';
import { useTributeVideoStore } from '@/stores/tributeVideo';
import { useTributeEditorStore } from '@/stores/tributeEditor';
import { useGlobalState } from '@/stores/globalState';
import MobilePreview from '@/components/Tribute/Layout/MobilePreview.vue';

export default {
    name: 'TributeView',
    setup() {
        return {
            tributeNotificationsStore: useTributeNotifications(),
            miniPlayer: useMiniPlayer(),
            tributeVideoStore: useTributeVideoStore(),
            tributeEditor: useTributeEditorStore(),
        };
    },
    metaInfo() {
        let title = 'Tribute Video';
        if (this.tributeVideo?.firstName && this.tributeVideo?.lastName) {
            title = `${this.tributeVideo.firstName} ${this.tributeVideo.lastName}'s Tribute`;
        }
        return {
            title,
        };
    },
    components: {
        TributeDeadlineChip,
        TributeStepper,
        TributeSetup,
        TributeSetupForContributors,
        ManagePhotos,
        CreatomatePreview,
        ManageThemes,
        ManageMusic,
        MiniMusicPlayer,
        StepCreate,
        ResizableWorkspace,
        TributeRenderOverlay,
        ChangelogPanel,
        ManageFinalVideo,
        ManageSummary,
        TributeDebugInfo,
        MobilePreview,
    },
    provide() {
        const tributeRender = {};
        const state = {};

        Object.defineProperty(tributeRender, 'standardRender', {
            enumerable: true,
            get: () => this.standardRender,
        });
        Object.defineProperty(tributeRender, 'publicRender', {
            enumerable: true,
            get: () => this.publicRender,
        });

        Object.defineProperty(state, 'isFuneralHomeAdmin', {
            enumerable: true,
            get: () => ['FuneralHome', 'SuperAdmin'].includes(this.$auth.role),
        });
        Object.defineProperty(state, 'isFamily', {
            enumerable: true,
            get: () => this.isFamilyPage,
        });
        Object.defineProperty(state, 'isContributor', {
            enumerable: true,
            get: () => this.isContributorPage,
        });
        Object.defineProperty(state, 'token', {
            enumerable: true,
            get: () => this.token,
        });
        Object.defineProperty(state, 'anonUserDetails', {
            enumerable: true,
            get: () => this.anonUserDetails,
        });
        Object.defineProperty(state, 'steps', {
            enumerable: true,
            get: () => this.steps,
        });
        Object.defineProperty(state, 'isMobile', {
            enumerable: true,
            get: () => this.isMobile,
        });
        Object.defineProperty(state, 'forceExpandedWorkspace', {
            enumerable: true,
            get: () => this.forceExpandedWorkspace,
        });
        Object.defineProperty(state, 'performanceMode', {
            enumerable: true,
            get: () => this.performanceMode,
        });
        Object.defineProperty(state, 'preview', {
            enumerable: true,
            get: () => ({
                mods: this.previewMods,
                source: this.previewSource,
            }),
        });
        Object.defineProperty(state, 'debugInfoEnabled', {
            enumerable: true,
            get: () => this.debugInfoEnabled,
        });
        return {
            tributeRender,
            state,
        };
    },
    computed: {
        miniPreviewVisible() {
            return (
                this.tributeEditorExpanded && !this.isMobile && !this.isContributorPage && !this.forceExpandedWorkspace
            );
        },
        showDeadline() {
            return (
                !this.isMobile &&
                this.tributeVideo.deadline &&
                (this.isContributorPage || this.isFamilyPage) &&
                this.currentStep === 'slides'
            );
        },
        forceExpandedWorkspace() {
            return (
                this.isContributorPage ||
                (this.isFamilyPage && this.workflow === this.tributeWorkflowEnum.FUNERAL_HOME) ||
                ['explore-media'].includes(this.currentStep) ||
                this.performanceMode === tributePerformanceModeEnum.PERFORMANCE
            );
        },
        showOutOfSyncWarning() {
            return this.outOfSyncChanges.length > 0 && !this.tributeEditorExpanded;
        },
        prevStep() {
            const curStepIndex = findIndex(this.steps, { hash: this.currentStep });
            if (this.steps?.[curStepIndex - 1]) {
                return this.steps?.[curStepIndex - 1];
            }
            return null;
        },
        nextStep() {
            const curStepIndex = findIndex(this.steps, { hash: this.currentStep });
            if (this.steps[curStepIndex + 1]) {
                return this.steps[curStepIndex + 1];
            }
            return null;
        },
        lastStep() {
            return this.steps[this.steps?.length - 1];
        },
        currentStepItem() {
            const currentStep = find(this.steps, { hash: this.currentStep });
            return currentStep;
        },
        currentStepIndex() {
            return findIndex(this.steps, { hash: this.currentStep });
        },
        workflow() {
            return this.tributeVideo?.tributeWorkflow != null ? this.tributeVideo.tributeWorkflow : 1;
        },
        isClosedSubmissionsAndFamilyOrContributor() {
            return !this.tributeVideo?.submissionsOpen && (this.isFamilyPage || this.isContributorPage);
        },
        lockdownSlidesPage() {
            if (this.isClosedSubmissionsAndFamilyOrContributor) return true;

            return false;
        },
        steps() {
            if (this.isClosedSubmissionsAndFamilyOrContributor && this.standardRender?.status === 2) {
                let allowedSteps = [];
                if (this.isFamilyPage) {
                    allowedSteps = ['final-video', 'explore-media'];
                } else if (this.isContributorPage) {
                    allowedSteps = ['final-video'];
                }

                return this.stepData.filter(step => {
                    const stepAllowed = allowedSteps.includes(step.hash);
                    if (step?.conditions?.length) {
                        let conditionsMet = !step.conditions.some(condition => (condition = false));
                        return stepAllowed && conditionsMet;
                    }
                    return stepAllowed;
                });
            }

            if (this.isFamilyPage) {
                return this.stepData.filter(
                    step =>
                        step.family &&
                        !step.hidden &&
                        step.workflows.includes(this.workflow) &&
                        !step.conditions?.some(condition => (condition = false)),
                );
            }
            if (this.isContributorPage) {
                let steps = this.stepData.filter(
                    step => step.contributor && !step.hidden && step.workflows.includes(this.workflow),
                );

                if (this.standardRender?.id && this.standardRender.status === creatomateRenderStatusEnum.SUCCEEDED) {
                    steps = steps.filter(step => step.hash !== 'create');
                }

                return steps;
            }

            return this.stepData.filter(step => step.default && !step.hidden);
        },
        stepHashes() {
            return this.steps.map(step => step.hash);
        },
        isFamilyPage() {
            return this.$route.name === 'TributeVideoFamily';
        },
        isContributorPage() {
            return this.$route.name === 'TributeVideoContributor';
        },
        showRenderOverlay() {
            // Show overlay if the following is true
            // we're on step 5
            // we've started and/or completed a render
            //
            if (this.currentStepIndex > 0) {
                if (this.tributeVideoStore.overlayDismissed) {
                    return false;
                }
                if (this.tributeIsRendering) {
                    return true;
                }
                if (this.tributeVideoStore.renderStarted && this.tributeVideoStore.renderCompleted) {
                    return true;
                }
            }
            return false;
        },
        tributeEditorExpanded() {
            //Force expanded state for contributors
            return this.tributeEditor.forceExpandedWorkspace || this.tributeEditor.expanded;
        },
        performanceMode() {
            return this.tributeVideoStore.performanceMode;
        },
        tributeVideo: {
            get() {
                return this.tributeVideoStore;
            },
            set(val) {
                this.tributeVideoStore.updateTributeVideo(val);
            },
        },
        minimumRequirementsMet() {
            return this.tributeVideo.minimumCreateRequirementsMet;
        },
        tributeIsRendering() {
            return this.tributeVideo.tributeIsRendering;
        },
        dueForADupeCheck() {
            return this.tributeVideo.dueForADupeCheck;
        },

        conditionallyDisableForContributors() {
            // return true to disable
            // if (this.isContributorPage || this.isFamilyPage) {
            //     if (!this.anonUserDetails?.name?.length) {
            //         return true;
            //     }
            //     if (!this.anonUserDetails?.relationship.length) {
            //         return true;
            //     }
            // }
            return false;
        },
        isMobile() {
            this.windowResizeCounter;
            const tmpIsMobile = window.matchMedia(`only screen and (((max-width: 768px) and (orientation: portrait))
                or ((max-height: 600px) and (orientation: landscape)))`).matches;

            return tmpIsMobile;
        },
        showSubmissionsClosedOverlay() {
            return (
                // family or contributor
                (this.isContributorPage || this.isFamilyPage) &&
                // submissions are closed
                !this.tributeVideo.submissionsOpen &&
                // final video is not rendered
                this.standardRender?.status !== 2 &&
                // currently on the slides page
                this.currentStep === 'slides' &&
                // other modals aren't showing
                !this.showEditModeOverlay
            );
        },
        showEditModeOverlay() {
            if (this.editModeWarningCanceled) {
                return false;
            }
            if (!this.standardRender?.id) {
                return false;
            }

            if (this.tributeVideo.submissionsOpen) {
                return false;
            }
            if (['create', 'final-video'].includes(this.currentStep)) {
                return false;
            }

            if (this.showRenderOverlay && this.tributeIsRendering) {
                return false;
            }

            // MEM-1573
            if (this.isContributorPage || this.isFamilyPage) {
                return false;
            }
            if (this.currentStep === 'setup') {
                return false;
            }
            return !this.editModeWarningDismissed;
        },
    },
    data() {
        return {
            creatomateRenderStatusEnum,
            tributeWorkflowEnum,
            tributePerformanceModeEnum,
            currentStep: this.$route.params.section || 'setup',
            // tributeVideo: null,
            mounted: false,
            standardRender: null,
            publicRender: null,
            apiService: null,
            token: null,
            previewSource: null,
            previewMods: null,
            editMode: false,
            loading: true,
            outOfSyncChanges: [],
            slideChanges: [],
            mainPhotoChanges: [],
            themeChanges: [],
            musicChanges: [],
            anonUserDetails: null,
            pinError: false,
            stepperRefreshKey: 0,
            confetti: false,
            confettiFired: false,
            confirmEditModeDismissModal: false,
            editModeWarningDismissed: false,
            editModeWarningCanceled: false,
            profileError: false,
            stepData: [
                {
                    name: "Person's Detail",
                    hash: 'setup',
                    default: true,
                    family: true,
                    contributor: true,
                    hidden: false,
                    workflows: [
                        tributeWorkflowEnum.FAMILY,
                        tributeWorkflowEnum.HYBRID,
                        tributeWorkflowEnum.FUNERAL_HOME,
                    ],
                },
                {
                    name: 'Manage Slides',
                    hash: 'slides',
                    default: true,
                    family: true,
                    contributor: true,
                    hidden: false,
                    workflows: [
                        tributeWorkflowEnum.FAMILY,
                        tributeWorkflowEnum.HYBRID,
                        tributeWorkflowEnum.FUNERAL_HOME,
                    ],
                },
                {
                    name: 'Set Theme',
                    hash: 'theme',
                    default: true,
                    family: true,
                    contributor: false,
                    hidden: false,
                    workflows: [tributeWorkflowEnum.FAMILY, tributeWorkflowEnum.HYBRID],
                },
                {
                    name: 'Add Music',
                    hash: 'music',
                    default: true,
                    family: true,
                    contributor: false,
                    hidden: false,
                    workflows: [tributeWorkflowEnum.FAMILY, tributeWorkflowEnum.HYBRID],
                },
                {
                    name: 'Summary',
                    hash: 'create',
                    default: true,
                    family: false,
                    contributor: false,
                    hidden: false,
                    workflows: [
                        tributeWorkflowEnum.FAMILY,
                        tributeWorkflowEnum.HYBRID,
                        tributeWorkflowEnum.FUNERAL_HOME,
                    ],
                },
                {
                    name: 'Summary',
                    hash: 'create',
                    default: false,
                    family: true,
                    contributor: true,
                    hidden: false,
                    workflows: [
                        tributeWorkflowEnum.FAMILY,
                        tributeWorkflowEnum.HYBRID,
                        tributeWorkflowEnum.FUNERAL_HOME,
                    ],
                    conditions: [
                        (this.isFamilyPage || this.isContributorPage) &&
                            !this.isClosedSubmissionsAndFamilyOrContributor,
                        this.standardRender?.status === 2,
                    ],
                },
                {
                    name: 'Final Video',
                    hash: 'final-video',
                    default: true,
                    family: true,
                    contributor: true,
                    hidden: true,
                    workflows: [
                        tributeWorkflowEnum.FAMILY,
                        tributeWorkflowEnum.HYBRID,
                        tributeWorkflowEnum.FUNERAL_HOME,
                    ],
                    conditions: [!this.submissionsOpen, this.standardRender?.status === 2],
                },
                {
                    name: 'Explore Photos & Videos',
                    hash: 'explore-media',
                    default: false,
                    family: true,
                    contributor: false,
                    hidden: true,
                    workflows: [
                        tributeWorkflowEnum.FAMILY,
                        tributeWorkflowEnum.HYBRID,
                        tributeWorkflowEnum.FUNERAL_HOME,
                    ],
                    conditions: [!this.submissionsOpen, this.standardRender?.status === 2],
                },
            ],
            tabTouchEvents: {
                left(evt) {
                    console.log(evt);
                },
                right(evt) {
                    console.log(evt);
                },
            },
            windowResizeCounter: 0,
            debugInfoEnabled: false,
            submissionsOpenChangeModal: false,
            sessionUser: null,
            longPollInterval: null,
            previewCurrentTime: 0,
            manageMusicRefreshKey: 0,
        };
    },
    methods: {
        ...mapActions(useGlobalState, ['showSnackbar', 'block']),
        // ...mapActions('tributeEditor', ['toggleExpandedState', 'setExpandedState', 'setForceExpanded']),

        previewRefreshEmitHandler() {
            if (!this.tributeVideo?.id || this.performanceMode !== tributePerformanceModeEnum.FEATURES) return;

            this.debouncedGeneratePreviewJson(this.tributeVideo.id);
        },
        cancelMakeChanges() {
            let tab = 'create';
            if (this.standardRender.id && this.standardRender.status === 2) {
                tab = 'final-video';
            }
            this.handleChangeStep(tab);
            this.$nextTick(() => {
                this.editModeWarningCanceled = true;
            });
        },
        async fetchSelectedPerformanceMode() {
            const mode = await getTributePerformanceMode();

            if (mode >= 0) {
                this.tributeVideoStore.performanceMode = mode;
            }
        },
        handleCreatomatePreviewTimeUpdate(val) {
            this.previewCurrentTime = val;
        },
        dismissEditModeWarning() {
            this.confirmEditModeDismissModal = false;
            this.editModeWarningDismissed = true;
        },
        startConfetti() {
            this.confetti = true;
            this.confettiFired = true;
            setTimeout(() => {
                this.confetti = false;
            }, 2140);
        },
        initRerender() {
            this.handleChangeStep('create');

            setTimeout(() => {
                const ref = this.$refs.manageSummary;
                if (ref && typeof ref.confirmRenderIfNeeded === 'function') {
                    ref.confirmRenderIfNeeded();
                }
            }, 200);
        },
        handleChangeStep(val) {
            // if (val < 0) return;
            // if (val > 5) return;
            if (!this.tributeVideo.id) return;
            // hook for sending family/contributors to final video after setting their details if standard render exists
            if (this.currentStep === 'setup' && (this.isContributorPage || this.isFamilyPage)) {
                if (this.standardRender) {
                    if (this.standardRender.status === 2) {
                        val = 'final-video';
                    } else {
                        val = this.lastStep.hash;
                    }
                }
            }
            this.currentStep = val;
            // Pause preview player if currently rendering and going back into the 5th section
            if (this.currentStep === this.lastStep.hash) {
                if (this.tributeIsRendering) {
                    this.pausePreview();
                }
            }
        },
        handleSetAnonUserDetails(userDetails) {
            this.anonUserDetails = userDetails;
            localStorage.setItem(
                `tributeContributor_${btoa(deburr(this.$route.params.serviceSlug))}`,
                JSON.stringify(userDetails),
            );
        },
        togglePreview() {
            this.tributeEditor.toggleExpandedState();
        },
        onRenderStarted() {
            // Do stuff when render starts
            this.pausePreview();
            this.initRenderLongPolling();
        },
        playPreview(instance = 'main') {
            if (!['main', 'summary'].includes(instance)) {
                throw new Error('Invalid preview instance');
            }

            const previewInstance =
                instance === 'main' ? this.$refs.mainCreatomatePreview : this.$refs.summaryCreatomatePreview;
            if (previewInstance && previewInstance.playVideo) {
                previewInstance.playVideo();
            }
        },
        pausePreview(instance = 'main') {
            if (!['main', 'summary'].includes(instance)) {
                throw new Error('Invalid preview instance');
            }

            const previewInstance =
                instance === 'main' ? this.$refs.mainCreatomatePreview : this.$refs.summaryCreatomatePreview;
            if (previewInstance && previewInstance.pauseVideo) {
                previewInstance.pauseVideo();
            }
        },
        updatePreviewTimestamp(val) {
            const previewInstance = this.$refs.mainCreatomatePreview;

            previewInstance?.setTime(val);
        },
        createTributeVideo() {
            // If not on the last step take them there first
            // then after delay try and trigger the render
            if (this.currentStep !== this.lastStep.hash) {
                this.currentStep = this.lastStep.hash;
            }
            this.$nextTick(() => {
                this.$refs.createStep.submitTributeRender();
            });
        },
        handleTributeCreated(data) {
            this.tributeVideo = data;

            if (this.tributeVideo.id) {
                this.debouncedGeneratePreviewJson(this.tributeVideo.id);
            }

            this.tryFetchTributeVideo(this.$route.params.slug);

            // need to try and update the selected theme
            this.getSelectedThemes();
        },

        async setAuthToken() {
            if (this.isContributorPage || this.isFamilyPage) {
                const tmpApi = TributeVideoService(null);
                try {
                    var existingSessionId = getTributeUploadSessionId();
                    if (!existingSessionId) return;

                    const { data } = await tmpApi.getTributeToken(
                        this.$route.params.slug,
                        this.$route.params.pin,
                        existingSessionId,
                    );

                    if (data && data.token) {
                        switch (data.role) {
                            case 'Tribute':
                                if (!this.isContributorPage) {
                                    // Don't allow them to continue show error page
                                    this.pinError = true;
                                    return false;
                                }
                                break;
                            case 'TributeAdmin':
                                if (!this.isFamilyPage) {
                                    // Don't allow them to continue show error page
                                    this.pinError = true;
                                    return false;
                                }
                                break;
                        }
                        this.token = data.token;
                        this.role = data.role;
                    }
                } catch (error) {
                    console.error(error);
                    this.pinError = true;
                }
            } else {
                const response = await this.$auth.getIdTokenClaims();
                if (response && response.__raw) {
                    this.token = response.__raw;
                }
            }
        },
        async refreshTributeDataLite(slug) {
            const resp = await this.apiService.tributeVideo.getTributeVideoByServiceSlug(slug);

            if (resp.data) {
                this.tributeVideo = resp.data.tributeVideo;
                this.tributeVideoStore.updateTributeVideo({
                    ...resp.data.tributeVideo,
                });
            }
        },
        async tryFetchTributeVideo(slug, block = false) {
            try {
                if (block) {
                    this.block(true);
                }
                const resp = await this.apiService.tributeVideo.getTributeVideoByServiceSlug(slug);
                if (resp.data) {
                    this.tributeVideo = resp.data.tributeVideo;
                    this.standardRender = resp.data.standardRender;

                    let shouldRefreshStepper = false;

                    if (this.standardRender?.id && this.standardRender.status === 2) {
                        const finalVidStep = find(this.stepData, { hash: 'final-video' });
                        if (finalVidStep) {
                            finalVidStep.hidden = false;
                            shouldRefreshStepper = true;
                        }
                        if (this.isClosedSubmissionsAndFamilyOrContributor && this.isFamilyPage) {
                            const exploreMedia = find(this.stepData, { hash: 'explore-media' });
                            if (exploreMedia) {
                                exploreMedia.hidden = false;
                                shouldRefreshStepper = true;
                            }
                        }
                    }

                    if (shouldRefreshStepper) {
                        this.stepperRefreshKey++;
                    }

                    this.publicRender = resp.data.copyrightSafeRender;

                    if (this.isContributorPage || this.isFamilyPage) {
                        if (isEmpty(this.anonUserDetails)) {
                            // send to setup
                            this.currentStep = 'setup';
                        }
                    } else {
                        if (this.standardRender) {
                            // console.log('RENDER READY', this.standardRender);
                            if (this.$route.params.section) {
                                this.currentStep = this.$route.params.section;
                            } else if (this.currentStep !== this.lastStep.hash) {
                                this.currentStep = this.lastStep.hash;
                            }
                        } else {
                            this.currentStep = this.$route.params.section || 'setup';
                        }
                    }

                    this.tributeVideoStore.updateTributeVideo({
                        ...resp.data.tributeVideo,
                    });
                } else {
                    const serviceResp = await this.apiService.services.getBySlug(slug);
                    if (serviceResp.data) {
                        this.tributeVideoStore.updateTributeVideo({
                            firstName: serviceResp.data.firstName,
                            lastName: serviceResp.data.lastName,
                        });
                    }
                }
            } catch (error) {
                console.error('Error fetching tribute video', error);
            } finally {
                this.block(false);
            }
        },
        async refreshPhotos() {
            if (!this.tributeVideo?.eventId) {
                return;
            }

            if (!this.$refs.managePhotos) return;

            this.$refs.managePhotos?.debouncedSocketRefresh();
        },
        async getSelectedThemes() {
            if (!this.tributeVideo?.id) {
                return;
            }
            const { data } = await this.apiService.tributeTemplate.getSelected(this.tributeVideo.id);
            if (data.templates?.length) {
                this.tributeVideoStore.updateTributeVideoSelectedTemplates(data.templates);
            }
        },
        async getSelectedSongs() {
            if (!this.tributeVideo?.id) {
                return;
            }
            const { data: songs } = await this.apiService.tributeSong.getSelected(this.tributeVideo.id);
            if (songs?.length) {
                this.tributeVideoStore.updateTributeVideoSelectedSongs(songs);
            }
        },
        async checkOutOfSync(tributeId) {
            const { data: res } = await this.apiService.tributeVideo.checkOutOfSync(tributeId);

            if (res.outOfSyncChanges) {
                this.outOfSyncChanges = res.outOfSyncChanges;
            }

            this.mainPhotoChanges = this.outOfSyncChanges.filter(
                x => x.action === tributeChangelogActionEnums.MAIN_PHOTO_UPDATE,
            );
            this.slideChanges = this.outOfSyncChanges.filter(x => x.tableName === 'TributeVideoPhoto');
            this.themeChanges = this.outOfSyncChanges.filter(x => x.tableName === 'SelectedTemplates');
            this.musicChanges = this.outOfSyncChanges.filter(x => x.tableName === 'SelectedSongs');
        },
        async tryGeneratePreviewJson(tributeId) {
            try {
                if (this.tributeVideo.uploadingPhotos || !this.token) return;

                if (!tributeId) {
                    throw new Error('Invalid tribute video id');
                }

                var resp = await this.apiService.tributeVideo.generatePreviewJson(tributeId);

                if (resp.data.duration) {
                    // store the duration on the tribute video store
                    this.tributeVideoStore.updateTributeVideo({
                        totalDuration: resp.data.duration,
                        slideDuration: resp.data.slideDuration,
                        introDuration: resp.data.intoDuration,
                        endingDurationTotal: resp.data.endingDurationTotal,
                        endingSlateDuration: resp.data.endingSlateDuration,
                        endingVideoDuration: resp.data.endingVideoDuration,
                        endingDuration: resp.data.endingDuration,
                        selectedAnimation: resp.data.selectedAnimation,
                        selectedTransition: resp.data.selectedTransition,
                        autoSlideDuration: resp.data.autoSlideDuration,
                    });
                }
                if (resp.data.creatomateJson) {
                    const creatomateJson = JSON.parse(resp.data.creatomateJson);

                    if (!creatomateJson.source) throw new Error('Error parsing preview source');

                    this.previewSource = creatomateJson.source;
                    console.log(this.previewSource, 'previewSource');

                    if (creatomateJson.modifications) {
                        this.previewMods = creatomateJson.modifications;
                    }
                }
            } catch (error) {
                console.log(error, 'error');
            }
        },
        updateProfileImageInStore(id, url) {
            this.tributeVideoStore.updateTributeVideo({
                mainPhotoUrl: url,
                mainPhotoId: id,
            });
        },
        async getSettings(tributeId) {
            try {
                const resp = await this.apiService.tributeVideo.getSettings(tributeId);
            } catch (error) {
                console.log(error, 'error getting tribute settings');
            }
        },
        validateRequestedSection() {
            const requestedStep = this.steps.find(x => x.hash === this.$route.params.section);

            if ((this.isContributorPage || this.isFamilyPage) && !this.sessionUser?.sessionId) {
                this.currentStep = 'setup';
                return;
            }

            if (!requestedStep) {
                this.currentStep = 'setup';
                return;
            }
        },
        handleChangelogNotification(data, eventType) {
            if (!this.standardRender?.id || !this.tributeVideo?.id || !this.tributeVideo?.eventId) return;

            switch (eventType) {
                case 'tributeThemeUpdate':
                case 'tributeSongsUpdate':
                case 'tributeRenderSettings':
                    if (!data.tributeId || data.tributeId !== this.tributeVideo.id) return;
                    this.debouncedOutOfSyncRefresh(this.tributeVideo.id);
                    break;
                case 'mainPhotoReplace':
                    if (!data.id || data.id !== this.tributeVideo.id) return;
                    this.debouncedOutOfSyncRefresh(this.tributeVideo.id);
                    break;
                case 'notifyUpload':
                    if (!data.id || data.id !== this.tributeVideo.eventId) return;
                    this.debouncedOutOfSyncRefresh(this.tributeVideo.id);
                    break;
                case 'tributeRender':
                    if (!data.tributeVideoId || data.tributeVideoId !== this.tributeVideo.id);
                    this.debouncedOutOfSyncRefresh(this.tributeVideo.id);
                    break;
                default:
                    console.log(eventType, 'unhandled changlelog notification type');
                    break;
            }
        },
        resumeRenderProgress() {
            console.log('resuming rendering');
            this.tributeVideoStore.cycleRenderMessages();

            if (this.tributeVideo.totalDuration > 0 && this.standardRender?.updateDate) {
                this.tributeVideoStore.updateRenderProgress({
                    started: this.standardRender?.updateDate,
                    duration: this.tributeVideo.totalDuration * 2,
                });
            }
        },
        async getSimilarSlides(serviceId) {
            try {
                if (!serviceId) throw new Error('Invalid serviceId');
                const response = await this.apiService.tributePhoto.getCollectionSimilars(serviceId);

                if (response.data) {
                    return response.data;
                }
            } catch (error) {
                console.log(error, 'error fetching similar photos');
            }
        },
        async getDuplicateSlides(serviceId) {
            try {
                if (!serviceId) throw new Error('Invalid serviceId');
                const response = await this.apiService.tributePhoto.getCollectionDuplicates(serviceId);

                if (response.data) {
                    return response.data;
                }
            } catch (error) {
                console.log(error, 'error fetching duplicate photos');
            }
        },
        async refreshDuplicateAndSimilarSlides(serviceId) {
            if (this.isContributorPage) return;
            // console.log('refreshDuplicateAndSimilarSlides', this.tributeVideo.slideCountWhenLastDupeCheck, this.tributeVideo.totalSlidesCount, this.dueForADupeCheck);
            if (!this.dueForADupeCheck) {
                console.warn(
                    `Skipping fetching of DuplicateAndSimilarSlides because:`,
                    `${!this.dueForADupeCheck ? ' not due for a dupe check' : ''}`,
                    `${
                        this.tributeVideo.slideCountWhenLastDupeCheck === this.tributeVideo.totalSlidesCount
                            ? 'slide count has not changed since last check.'
                            : ''
                    }`,
                );
                return;
            }
            // console.log('refreshing dupes & similars from TributeVideo.vue');
            this.tributeVideoStore.updateTributeVideo({
                slideCountWhenLastDupeCheck: this.tributeVideo.totalSlidesCount || 0,
                lastDupeCheckTimestamp: new Date().toISOString(),
            });
            const dupes = await this.getDuplicateSlides(serviceId);
            const similars = await this.getSimilarSlides(serviceId);

            this.duplicateSlideGroups = {
                results: dupes?.results || [],
                total: dupes?.total || 0,
            };

            this.similarSlideGroups = {
                results: similars?.results || [],
                total: similars?.total || 0,
            };

            let totalDupes = 0;
            let totalSimilars = 0;
            if (dupes?.results) {
                totalDupes = dupes.results.reduce((acc, current) => acc + current.count, 0);
                totalSimilars = similars.results.reduce((acc, current) => acc + current.count, 0);
            }

            this.tributeVideoStore.updateTributeVideo({
                duplicateSlideCount: totalDupes,
                similarSlideCount: totalSimilars,
                duplicateSlideGroups: this.duplicateSlideGroups,
                similarSlideGroups: this.similarSlideGroups,
            });
        },
        resizeHandler: debounce(function (event) {
            this.windowResizeCounter++;
        }, 500),
        async getIngestPipelineCount() {
            try {
                if (!this.tributeVideo?.eventId) return;
                const resp = await this.apiService.tributePhoto.ingestPipelineCount(this.tributeVideo.eventId);

                if (resp.data) {
                    this.tributeVideoStore.updateTributeVideo({
                        ingestPipelineCount: resp.data.count,
                    });
                }
            } catch (error) {
                console.log(error, 'error');
            }
        },
        openSettingsModal() {
            this.$root.$emit('tribute:open-settings-modal');
        },
        handleStorageChange(event) {
            if (event.key === 'showDebugInfo') {
                this.debugInfoEnabled = event.newValue === 'true';
            }
        },
        async fetchCurrentTributeUser() {
            const sessionId = getTributeUploadSessionId();

            console.log(sessionId, 'sessionId at fetch');
            if (!sessionId) {
                return;
            }

            // var resp = await this.apiService.tributeVideo.getUserCurrent(sessionId);
            const apiInstance = this.axios.create({
                baseURL: process.env.VUE_APP_API,
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            let slug = this.$route.params.slug;
            let uploadPin = this.$route.params.pin;

            var resp = await apiInstance.get(`/TributeVideo/users/current/${slug}/${uploadPin}/${sessionId}`);

            if (resp.data) {
                this.sessionUser = resp.data.user;
            }
        },
        async createCurrentTributeUser() {
            const apiInstance = this.axios.create({
                baseURL: process.env.VUE_APP_API,
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            let slug = this.$route.params.slug;
            let uploadPin = this.$route.params.pin;

            var resp = await apiInstance.get(`/TributeVideo/users/create-from-session/${slug}/${uploadPin}`);

            if (resp.data) {
                this.sessionUser = resp.data.user;
            }

            if (this.sessionUser?.sessionId) {
                setTributeUploadSessionId(this.sessionUser.sessionId);
            }
        },
        async setSubmissionsOpen(bool) {
            try {
                const id = this.tributeVideo.id;

                if (!id) {
                    throw new Error('Invalid tribute id');
                }
                const resp = await this.apiService.tributeVideo.setTributeSubmissionsOpen(id, bool);

                if (resp.data) {
                    this.tributeVideo.submissionsOpen = resp.data.submissionsOpen;
                    this.tributeVideoStore.updateTributeVideo({
                        submissionsOpen: resp.data.submissionsOpen,
                    });
                }

                this.showSnackbar({ message: 'Submissions settings updated' });
                this.submissionsOpenChangeModal = false;
            } catch (error) {
                console.log('error', error);
            }
        },
        async updateSessionUser(user) {
            this.sessionUser = user;
            this.initBaseComponent();
        },
        async initBaseComponent() {
            this.block(true);
            this.fetchSelectedPerformanceMode();

            if (this.isFamilyPage || this.isContributorPage) {
                await this.fetchCurrentTributeUser();
                if (this.sessionUser && !this.anonUserDetails) {
                    // Check localstorage for stored user details
                    const tributeUserDetails = localStorage.getItem(
                        `tributeContributor_${btoa(deburr(this.$route.params.serviceSlug))}`,
                    );
                    if (typeof tributeUserDetails === 'string') {
                        this.anonUserDetails = JSON.parse(tributeUserDetails);
                    }
                }
            }

            await this.setAuthToken();
            this.apiService = initApiServices(this.token);

            window.addEventListener('storage', this.handleStorageChange);
            // debug tooling check
            if (location.search.includes('enable-debug-info')) {
                this.debugInfoEnabled = true;
                // clear out the query string?
                // this.$router.replace({'query': null});
                sessionStorage.setItem('showDebugInfo', 'true');
            } else if (sessionStorage.getItem('showDebugInfo') === 'true') {
                this.debugInfoEnabled = true;
            }
            if (
                this.isMobile ||
                this.isContributorPage ||
                this.forceExpandedWorkspace ||
                this.performanceMode === tributePerformanceModeEnum.PERFORMANCE
            ) {
                // Force expanded state on mobile and for contributors anf for family when workflow set to fh does everything
                this.tributeEditor.setExpandedState(true);
            }
            window.addEventListener('resize', this.resizeHandler);

            await this.checkRenderStateChange();

            this.validateRequestedSection();

            this.mounted = true;
            if (this.tributeVideo?.id && this.token) {
                this.tributeNotificationsStore.clearNotifications();
                await this.tryGeneratePreviewJson(this.tributeVideo.id);
                // Fetch selected Music, & Themes
                // Themes
                this.getSelectedThemes();
                // Music
                this.getSelectedSongs();
            }

            if (this.tributeVideo?.serviceId && this.token) {
                this.refreshDuplicateAndSimilarSlides(this.tributeVideo.serviceId);
            }
            await this.$nextTick();
            this.loading = false;
            this.block(false);

            this.$root.$on('custom-tribute-event', (eventName, payload) => {
                switch (eventName) {
                    case 'goToDuplicateSlides':
                        this.currentStep = 'slides';
                        setTimeout(() => {
                            this.$refs.managePhotos.openGroupResolveModal(payload);
                        }, 600);
                        break;
                }
            });
            this.$root.$on('enableDebugInfo', val => {
                this.debugInfoEnabled = val;
                sessionStorage.setItem('showDebugInfo', `${val}`);
            });
        },
        async checkRenderStateChange(signalREventData = null) {
            const cachedStandardRender = this.standardRender;

            await this.tryFetchTributeVideo(this.$route.params.slug);

            if (!this.standardRender?.id) return;

            if (
                cachedStandardRender?.url === this.standardRender?.url &&
                cachedStandardRender?.status === this.standardRender?.status
            ) {
                return;
            }

            this.checkOutOfSync(this.tributeVideo.id);

            if (
                this.standardRender?.status === creatomateRenderStatusEnum.RENDERING ||
                this.standardRender?.status === creatomateRenderStatusEnum.PLANNED
            ) {
                if (!this.tributeVideo.renderStarted) {
                    this.resumeRenderProgress();
                }
            } else {
                this.cleanUpLongPolling();
            }

            if (this.standardRender?.status === creatomateRenderStatusEnum.SUCCEEDED) {
                this.tributeVideoStore.setRenderCompleted();
                this.tributeNotificationsStore.clearNotifications();

                if (
                    cachedStandardRender?.status === creatomateRenderStatusEnum.RENDERING ||
                    cachedStandardRender?.status === creatomateRenderStatusEnum.PLANNED
                ) {
                    this.handleChangeStep('final-video');
                    this.manageMusicRefreshKey++;
                }
            }

            if (signalREventData) {
                this.processRenderStatusChange(signalREventData);
            } else if (cachedStandardRender?.status != null) {
                this.processRenderStatusChange(this.standardRender);
            }
        },

        processRenderStatusChange(renderData) {
            if (this.tributeVideo.id != renderData.tributeVideoId) return;

            // await this.checkRenderStateChange();
            switch (renderStatusDict[renderData.status]) {
                // TODO: See if Planned & Rendering should set the renderOverlay...
                // Usecase: starting a render in a different tab or by another user?
                case 'Planned':
                    break;
                case 'Rendering':
                    break;
                case 'Succeeded':
                    // this.$nextTick(async () => {
                    // update the tributestore

                    // We've recieved the socket event, and we've fetched the latest json
                    // So if we have a standard render and url... can we safely assume that it has finished?
                    // Also checking Id of the payload so that we don't get double confetti for public & standard renders
                    // TODO: We should also check the `renderType` prop but that isn't set to public when there's only Standard render right now
                    if (this.standardRender?.url && renderData.id === this.standardRender?.id) {
                        this.startConfetti();
                        this.showSnackbar({ message: 'Tribute Video render finished!' });
                    }
                    // If this is the "public" render and standard has already finished let's give them a toast
                    if (this.publicRender?.id && renderData.id === this.publicRender.id) {
                        if (this.standardRender?.status === 2) {
                            this.showSnackbar({
                                message: 'The public version has finished rendering and is available to share.',
                                timeout: 4000,
                            });
                        }
                    }

                    break;
                case 'Failed':
                case 'Delete':
                    // Render failed or was deleted, we should notify user and remove render overlay
                    this.showSnackbar({
                        message: 'Rendering the video has failed. Please wait and try again.',
                        color: 'error',
                    });
                    this.tributeVideoStore.updateTributeVideo({
                        renderStarted: null,
                        renderCompleted: null,
                        overlayDismissed: true,
                    });

                    break;
            }
        },
        async handleVisibilityChange() {
            if (document.hidden) {
                // Tab became inactive - maybe reduce activity
                console.log('Tab hidden');

                this.cleanUpLongPolling();
            } else {
                // Tab became active - maybe reconnect if needed
                console.log('Tab visible');
                console.log(this.tributeVideo);

                if (
                    (this.tributeVideo.renderStarted && !this.renderCompleted) ||
                    (this.standardRender?.id && this.standardRender.status < 2)
                ) {
                    this.initRenderLongPolling();
                }
            }
        },
        async initRenderLongPolling() {
            this.cleanUpLongPolling();

            await this.checkRenderStateChange();

            // Fallback long polling in case signalR event misses or tab is suspended
            this.longPollInterval = setInterval(async () => {
                await this.checkRenderStateChange();
            }, 90000);
            console.log('initiating long poll interval', this.longPollInterval);
        },
        cleanUpLongPolling() {
            console.log('destroying long poll interval');
            clearInterval(this.longPollInterval);
        },
    },
    created() {
        this.debouncedGeneratePreviewJson = debounce(this.tryGeneratePreviewJson, 400);
        this.debouncedOutOfSyncRefresh = debounce(this.checkOutOfSync, 1000);
        this.debouncedUpdateProfileInStore = debounce(this.updateProfileImageInStore, 400);
        this.debouncedRefreshDuplicateAndSimilarSlides = debounce(this.refreshDuplicateAndSimilarSlides, 400);
        this.debouncedGetIngestPipelineCount = debounce(this.getIngestPipelineCount, 700);
        this.debouncedRefreshTributeDataLite = debounce(this.refreshTributeDataLite, 700);
        this.debouncedTryFetchTributeVideo = debounce(this.tryFetchTributeVideo, 700);
    },
    async mounted() {
        this.initBaseComponent();

        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    },
    beforeDestroy() {
        this.tributeVideoStore.clearIntervals();
        window.removeEventListener('resize', this.resizeHandler);
        window.removeEventListener('storage', this.handleStorageChange);

        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    },
    sockets: {
        async NotifyTributeRender(data) {
            if (this.tributeVideo.id == data.tributeVideoId) {
                this.checkRenderStateChange(data);
                this.handleChangelogNotification(data, 'tributeRender');
            }
        },
        async NotifyRenderStatus(data) {
            if (
                data.eventId === this.tributeVideo.eventId &&
                data.engineType === 4 &&
                (data.videoQueStatus === 9 || data.videoQueStatus === 10)
            ) {
                this.checkRenderStateChange(data);
            }
        },
        async NotifyTributeJsonRefreshed(data) {
            //Hold preview refresh untill batched upload is finished
            if (this.tributeVideo.uploadingPhotos) return;

            if (this.tributeVideo?.id && data.id == this.tributeVideo.id) {
                // Only auto reload preview player for "Features" performance mode
                if (this.performanceMode === tributePerformanceModeEnum.FEATURES) {
                    this.debouncedGeneratePreviewJson(this.tributeVideo.id);
                }

                if (this.tributeVideo.serviceId && data.serviceId == this.tributeVideo.serviceId) {
                    this.debouncedRefreshDuplicateAndSimilarSlides(this.tributeVideo.serviceId);
                }

                if (this.profileError) {
                    this.profileError = false;
                    this.debouncedRefreshTributeDataLite(this.$route.params.slug);
                }
            }
        },
        NotifyUpload(data) {
            this.handleChangelogNotification(data?.event, 'notifyUpload', data);

            if (this.tributeVideo.eventId && data?.event?.id === this.tributeVideo.eventId) {
                // Reset dupe slide Count to force a check
                if (data.action === 'Upload' || data.action === 'Delete') {
                    this.tributeVideoStore.updateTributeVideo({
                        slideCountWhenLastDupeCheck: -1,
                    });
                }
                this.refreshPhotos();
            }
        },
        NotifyMainPhotoReplace(data) {
            this.handleChangelogNotification(data?.tributeVideo, 'mainPhotoReplace');

            if (this.tributeVideo.id && data?.tributeVideo?.id === this.tributeVideo.id) {
                let mainPhotoId = 0;
                let mainPhotoUrl = null;

                if (data?.slides?.length > 0) {
                    mainPhotoId = data?.slides[0].id;
                    mainPhotoUrl = data?.slides[0].url;
                }
                this.debouncedUpdateProfileInStore(mainPhotoId, mainPhotoUrl);
            }
        },
        NotifyTributeThemeUpdate(data) {
            this.handleChangelogNotification(data, 'tributeThemeUpdate');
        },
        NotifyTributeSongsUpdate(data) {
            this.handleChangelogNotification(data, 'tributeSongsUpdate');
        },
        NotifyTributeRenderSettings(data) {
            this.handleChangelogNotification(data, 'tributeRenderSettings');
        },
        NotifyIngestPipelineUpdate(data) {
            if (data.eventId === this.tributeVideo.eventId) {
                console.log('ingest heard');
                this.debouncedGetIngestPipelineCount();
            }
        },
        NotifyTributeSettings(data) {
            if (
                this.tributeVideo.id &&
                data.id === this.tributeVideo.id &&
                data?.submissionsOpen !== this.tributeVideo?.submissionsOpen
            ) {
                this.debouncedTryFetchTributeVideo(this.$route.params.slug, true);
            }
        },
    },
    watch: {
        forceExpandedWorkspace(newVal) {
            if (newVal) {
                this.tributeEditor.toggleExpandedState(true);
            }
            this.tributeEditor.setForceExpanded(newVal);
        },
        workflow(newVal) {
            this.stepperRefreshKey++;
        },
        // Update route with current step as it changes
        currentStep(newVal, oldVal) {
            // If no session user @ family or contributor page, force setup
            if ((this.isContributorPage || this.isFamilyPage) && newVal != 'setup' && !this.sessionUser?.sessionId) {
                this.currentStep = 'setup';
                return;
            }

            if (newVal !== oldVal && this.$route.params.section !== newVal) {
                const currentParams = this.$route.params;
                currentParams.section = newVal;
                this.$router.push({ name: this.$route.name, params: currentParams });
            }

            // If user is going to final step, and the workarea is expanded
            // let's collapse the resizableWorkare for them so that the preview is visible
            if (
                newVal === 'create' &&
                oldVal !== 'create' &&
                this.tributeEditorExpanded &&
                !this.isMobile &&
                !this.isContributorPage &&
                !this.forceExpandedWorkspace
            ) {
                this.tributeEditor.toggleExpandedState();
            }

            const isLeavingMusicTab = oldVal === 'music' && newVal !== 'music';
            // const isEnteringMusicTab = newVal === 'music' && oldVal !== 'music';

            if (isLeavingMusicTab) {
                // Pause music and hide mini player when leaving music tab
                if (!this.miniPlayer.isPlaying) {
                    const player = this.$refs.miniMusicPlayer;

                    if (player) {
                        player.stop();
                    }
                }
            }

            if (newVal === 'create' && oldVal !== 'create') {
                this.pausePreview('main');

                this.debouncedGetIngestPipelineCount();
                this.tryFetchTributeVideo(this.$route.params.slug);
            }

            if (oldVal === 'create') {
                this.pausePreview('summary');
            }

            if (newVal === 'setup') {
                this.pausePreview('main');
            }

            if (oldVal === 'final-video' && newVal !== 'final-video') {
                this.pausePreview('main');

                const playerRef = this.$refs.finalVideoPlayer;

                if (playerRef && playerRef.pause) {
                    playerRef.pause();
                }
            }

            if (oldVal !== 'final-video' && newVal === 'final-video') {
                this.pausePreview('main');
                this.pausePreview('summary');

                const previewRef = this.$refs.creatomatePreview;

                if (previewRef && previewRef.pauseVideo) {
                    previewRef.pauseVideo();
                }
            }
            // If user only canceled edit mode modal, but hasn't enabled edit mode
            // then we need to keep prompting them until they enter edit mode
            if (!this.editModeWarningDismissed && this.editModeWarningCanceled) {
                this.editModeWarningCanceled = false;
            }
        },
        isMobile(newVal, oldVal) {
            if (newVal && newVal !== oldVal) {
                if (!this.tributeEditorExpanded) {
                    this.tributeEditor.setExpandedState(true);
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    .v-card__title {
        @include tribute-heading;
    }

    button,
    .v-btn,
    .v-btn__content {
        @include no-text-transform;
    }
}

.primary-btn {
    @include tribute-btn($btn-orange, $textColor: white);
}
.secondary-btn {
    @include tribute-btn($light-gray, $borderColor: transparent);
}
.error-btn {
    @include tribute-btn($btn-red, $textColor: white);
}
.tribute-view {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100dvh;
    box-sizing: border-box;
    background-color: #f3f4f6;
    overflow-y: auto;
    overflow-x: hidden;
    @include mobile() {
        overscroll-behavior: none;
    }
}

.main-content {
    position: relative;
    width: 100%;
    height: calc(100% - 72px);
    max-height: calc(100% - 72px);

    display: flex;
    flex-direction: column;
    align-items: center;
    @include mobile() {
        &.expanded {
            height: auto;
        }
    }
    &.section-create {
        height: auto;
        flex: 1;
    }
    .v-overlay.v-overlay--active {
        @include mobile() {
            // height is full height minus top & bottom navs
            height: calc(100dvh - (72px + 60px));
            position: fixed;
            top: 72px;
        }
    }
}

.step-content {
    width: 100%;
    // display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    min-height: 0;
    flex: 1;
    max-height: calc(100dvh - 75px);
    // margin: 12px;
    overflow: auto;
    position: relative;

    &:empty {
        max-height: 8dvh;
    }
    @include mobile() {
        //padding-top: 80px;
    }
}

.section-setup .step-content {
    display: flex;
}
.section-create,
.section-final-video {
    .step-content {
        align-content: center;
    }
}
.final-video-container {
    max-height: 100%;
    margin-top: 15px;
}

.center-column {
    max-width: calc(100% - 240px);
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 12px;
    min-height: 0;
    height: 100%;
    max-height: 100%;
}

.summary-preview {
    height: 100%;
}

.preview-player-container {
    height: 100%;
    width: 100%;
    padding: 12px;
}

.preview-player {
    height: 100%;
    width: 100%;
}

.middle-content {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    max-height: calc(68dvh - 140px);
    padding: 1rem;
    overflow: hidden;

    .action-column {
        min-width: 120px;
        text-align: center;
        align-content: center;
        flex-shrink: 0;
    }

    &.expanded {
        max-height: fit-content;
        .preview-player {
            height: 0;
            overflow: hidden;
            display: none;
        }
    }
    @include mobile() {
        &:not(.expanded) {
            flex-direction: row;
            flex-wrap: wrap;

            // .preview-player {
            //     order: 0;
            //     flex: 1 1 100%;
            // }
            .action-column {
                order: 1;
                flex: 1 0 50%;
            }
        }
        &.expanded {
            justify-content: center;
        }
        .action-column {
            display: none;
        }
    }
}
.play-icon {
    // color: $primary-grey;
}
.next-btn {
    background-color: $primary-orange !important;
    color: white !important;
}

.changelog-panel-activator {
    position: fixed;
    bottom: 12px;
    right: 12px;
}

.workspace {
    // position: sticky;
    // bottom: 0;
    width: 100%;
    max-height: 100%;
}

.out-of-sync-warning-outer {
    border: 1px solid #fcd34d;
    background-color: #fffbeb;
    color: #92400e;
    gap: 8px;
    padding: 4px;
    cursor: pointer;
    font-size: 12px;

    .changes-detected-pill {
        background-color: #fef3c7;
        padding: 2px 10px;
    }
}
.loading-overlay {
    display: flex;
    height: 100dvh;
    align-items: center;
}
::v-deep {
    .styled-videojs {
        .vjs-error-display,
        .vjs-modal-dialog-content {
            z-index: 0;
        }
    }
}
.tribute-error-display {
    display: flex;
    flex-direction: column;
    gap: 10px;
    position: absolute;
    inset: 0;
    align-items: center;
    justify-content: center;
}

.confetti {
    &:before {
        content: '';
        background: url('https://memoryshareprod.blob.core.windows.net/front-end-assets/confetti.gif') no-repeat bottom
            center;
        background-size: cover;
        inset: 0;
        margin: auto;
        position: absolute;
        z-index: 9;
    }
}

.fh-branding {
    width: 100%;
    max-height: 120px;
    max-width: 100%;

    overflow: hidden;
    object-fit: contain;
    display: flex;
    justify-content: center;

    .fh-logo {
        width: 100%;
        max-width: 300px;
        max-height: 100%;
        overflow: hidden;
        object-fit: contain;
    }
}
.status-tag {
    background: #d5d5d5;
    padding: 3px 4px;
    border-radius: 5px;
    font-weight: bold;
}
// @media (max-height: 950px) {
//     .center-column {
//         width: 80vh;
//     }
// }
.submissions-control {
    .label {
        font-weight: bold;
        margin-right: 10px;
    }
    border: 1px solid #000;
    border-radius: 3px;
    padding: 0 10px;
    height: 50px;

    &::v-deep {
        .v-label {
            margin: 0;
        }
    }
}
@media screen and (max-width: 1160px) {
    .submissions-control {
        &::v-deep {
            .v-label {
                display: none !important;
            }
        }
    }
}
@media screen and (max-width: 1010px) {
    .submissions-control {
        flex-direction: column;
        padding: 2px 0;
        .label {
            margin: 0;
            padding: 0 2px;
        }

        .v-input {
            &::v-deep {
                margin: 0;
                margin-top: -8px;
            }
        }
        .v-messages {
            display: none;
        }
    }
}
.changelog-control {
    width: fit-content;
    margin-left: auto;
    margin-right: auto;
}
</style>
